// src/components/CreateCompetitionDialog.js
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    FormControlLabel,
    Checkbox,
    Box,
    Typography,
    IconButton,
    Chip,
    RadioGroup,
    Radio,
    FormControl,
    FormLabel,
    DialogContentText,
    Alert
} from '@mui/material';
import { Close, Add } from '@mui/icons-material';
import { createCompetition, updateCompetition } from '../api/competitionApi';
import TextareaAutosize from "@mui/material/TextareaAutosize";

export default function CreateCompetitionDialog({ open, onClose, affiliateId, competition }) {
    const [formData, setFormData] = useState({
        name: '',
        location: '',
        startDate: '',
        endDate: '',
        maxParticipants: 50,
        isTeamCompetition: false,
        teamSize: 2,
        allowSelfScoring: false,
        allowJudgeSelfRegistration: false,
        organizer: '',
        description: '',
        forMembersOnly: false,
        scoringSystem: {
            firstPlacePoints: 100,
            decrementBy: 1,
            higherIsBetter: true
        },
        categories: []
    });

    const [newCategory, setNewCategory] = useState('');
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        if (competition) {
            setFormData({
                name: competition.name || '',
                location: competition.location || '',
                startDate: competition.startDate ? new Date(competition.startDate).toISOString().split('T')[0] : '',
                endDate: competition.endDate ? new Date(competition.endDate).toISOString().split('T')[0] : '',
                maxParticipants: competition.maxParticipants || 50,
                isTeamCompetition: competition.isTeamCompetition || false,
                teamSize: competition.teamSize || 2,
                allowSelfScoring: competition.allowSelfScoring || false,
                allowJudgeSelfRegistration: competition.allowJudgeSelfRegistration || false,
                organizer: competition.organizer || '',
                description: competition.description || '',
                forMembersOnly: competition.forMembersOnly || false,
                scoringSystem: competition.scoringSystem || {
                    firstPlacePoints: 100,
                    decrementBy: 1,
                    higherIsBetter: true
                },
                categories: competition.categories || []
            });
        } else {
            // Reset form for new competition
            setFormData({
                name: '',
                location: '',
                startDate: '',
                endDate: '',
                maxParticipants: 50,
                isTeamCompetition: false,
                teamSize: 2,
                allowSelfScoring: false,
                allowJudgeSelfRegistration: false,
                organizer: '',
                description: '',
                forMembersOnly: false,
                scoringSystem: {
                    firstPlacePoints: 100,
                    decrementBy: 1,
                    higherIsBetter: true
                },
                categories: []
            });
        }
    }, [competition, open]);

    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleScoringSystemChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            scoringSystem: {
                ...prev.scoringSystem,
                [field]: value
            }
        }));
    };

    const handleAddCategory = () => {
        if (newCategory.trim() && !formData.categories.includes(newCategory.trim())) {
            setFormData(prev => ({
                ...prev,
                categories: [...prev.categories, newCategory.trim()]
            }));
            setNewCategory('');
        }
    };

    const handleRemoveCategory = (categoryToRemove) => {
        setFormData(prev => ({
            ...prev,
            categories: prev.categories.filter(cat => cat !== categoryToRemove)
        }));
    };

    const handleSubmit = async () => {
        if (!formData.name || !formData.location || !formData.startDate || !formData.endDate || !formData.organizer) {
            setAlertMessage('Please fill in all required fields');
            setAlertOpen(true);
            return;
        }

        if (formData.categories.length === 0) {
            setAlertMessage('Please add at least one competition category');
            setAlertOpen(true);
            return;
        }

        try {
            const payload = {
                ...formData,
                affiliateId: parseInt(affiliateId)
            };

            if (competition) {
                await updateCompetition(competition.id, payload);
            } else {
                await createCompetition(payload);
            }

            setErrorMessage('');
            onClose();
        } catch (error) {
            console.error('Error saving competition:', error);
            setErrorMessage('Failed to save competition');
        }
    };

    const handleAlertClose = () => {
        setAlertOpen(false);
        setAlertMessage('');
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">
                        {competition ? 'Edit Competition' : 'Create New Competition'}
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <Close />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                {errorMessage && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage('')}>
                        {errorMessage}
                    </Alert>
                )}

                <Grid container spacing={2}>
                    {/* Basic Info */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Competition Name *"
                            value={formData.name}
                            onChange={(e) => handleChange('name', e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Location *"
                            value={formData.location}
                            onChange={(e) => handleChange('location', e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Organizer *"
                            value={formData.organizer}
                            onChange={(e) => handleChange('organizer', e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            type="date"
                            label="Start Date *"
                            value={formData.startDate}
                            onChange={(e) => handleChange('startDate', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            type="date"
                            label="End Date *"
                            value={formData.endDate}
                            onChange={(e) => handleChange('endDate', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Description
                        </Typography>
                        <TextareaAutosize
                            minRows={3}
                            value={formData.description}
                            onChange={(e) => handleChange('description', e.target.value)}
                            placeholder="Enter competition description..."
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '4px',
                                borderColor: '#AAAAAA',
                                fontSize: '14px',
                                lineHeight: '1.5',
                                resize: 'vertical',
                                fontFamily: 'inherit'
                            }}
                        />
                    </Grid>

                    {/* Participants */}
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Max Participants *"
                            value={formData.maxParticipants}
                            onChange={(e) => handleChange('maxParticipants', parseInt(e.target.value))}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.isTeamCompetition}
                                    onChange={(e) => handleChange('isTeamCompetition', e.target.checked)}
                                />
                            }
                            label="Team Competition"
                        />
                        {formData.isTeamCompetition && (
                            <TextField
                                fullWidth
                                type="number"
                                label="Team Size"
                                value={formData.teamSize}
                                onChange={(e) => handleChange('teamSize', parseInt(e.target.value))}
                                sx={{ mt: 1 }}
                            />
                        )}
                    </Grid>

                    {/* Scoring System */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            Scoring System
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            type="number"
                            label="First Place Points"
                            value={formData.scoringSystem.firstPlacePoints}
                            onChange={(e) => handleScoringSystemChange('firstPlacePoints', parseInt(e.target.value))}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Points Decrement"
                            value={formData.scoringSystem.decrementBy}
                            onChange={(e) => handleScoringSystemChange('decrementBy', parseInt(e.target.value))}
                            helperText="Points decrease per position"
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <FormControl component="fieldset">
                            <FormLabel component="legend">Points Direction</FormLabel>
                            <RadioGroup
                                value={formData.scoringSystem.higherIsBetter ? 'higher' : 'lower'}
                                onChange={(e) => handleScoringSystemChange('higherIsBetter', e.target.value === 'higher')}
                            >
                                <FormControlLabel value="higher" control={<Radio />} label="Higher is Better" />
                                <FormControlLabel value="lower" control={<Radio />} label="Lower is Better" />
                            </RadioGroup>
                        </FormControl>
                    </Grid>

                    {/* Categories */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            Competition Categories *
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Category Name"
                                value={newCategory}
                                onChange={(e) => setNewCategory(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleAddCategory();
                                    }
                                }}
                            />
                            <Button
                                variant="contained"
                                startIcon={<Add />}
                                onClick={handleAddCategory}
                            >
                                Add
                            </Button>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {formData.categories.map((category, index) => (
                                <Chip
                                    key={index}
                                    label={category}
                                    onDelete={() => handleRemoveCategory(category)}
                                    color="primary"
                                />
                            ))}
                        </Box>
                    </Grid>

                    {/* Settings */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            Settings
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formData.allowSelfScoring}
                                        onChange={(e) => handleChange('allowSelfScoring', e.target.checked)}
                                    />
                                }
                                label="Allow participants to submit their own scores"
                            />

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formData.forMembersOnly}
                                        onChange={(e) => handleChange('forMembersOnly', e.target.checked)}
                                    />
                                }
                                label="For members only (only affiliate members can see and register)"
                            />

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formData.allowJudgeSelfRegistration}
                                        onChange={(e) => handleChange('allowJudgeSelfRegistration', e.target.checked)}
                                    />
                                }
                                label="Allow users to register themselves as judges"
                            />
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={handleSubmit}>
                    {competition ? 'Update' : 'Create'} Competition
                </Button>
            </DialogActions>

            {/* Alert Dialog */}
            <Dialog
                open={alertOpen}
                onClose={handleAlertClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        maxWidth: { xs: '90vw', sm: '500px' }
                    }
                }}
            >
                <DialogTitle
                    id="alert-dialog-title"
                    sx={{
                        fontWeight: 'medium',
                        fontSize: { xs: '1.1rem', sm: '1.25rem' }
                    }}
                >
                    Warning
                </DialogTitle>
                <DialogContent>
                    <DialogContentText
                        id="alert-dialog-description"
                        sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' } }}
                    >
                        {alertMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: { xs: 2, sm: 3 }, gap: 1 }}>
                    <Button
                        onClick={handleAlertClose}
                        color="primary"
                        variant="contained"
                        autoFocus
                        sx={{
                            borderRadius: 20,
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                            },
                            transition: 'all 0.2s'
                        }}
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
}